import { Layout } from "@/components/layout/Layout";
import { HubCard } from "@/components/shared/HubCard";
import { SEOHead } from "@/components/shared/SEOHead";
import { FreeBanner } from "@/components/shared/FreeBanner";
import { Button } from "@/components/ui/button";
import { toolHubs, getTotalTools, getAIToolCount, getClientToolCount } from "@/data/toolHubs";

import {
  ArrowRight,
  Sparkles,
  Zap,
  Shield,
  Globe,
  Clock,
  Users,
} from "lucide-react";

const totalTools = getTotalTools();
const aiTools = getAIToolCount();
const clientTools = getClientToolCount();

const features = [
  {
    icon: Sparkles,
    title: "AI-Powered Tools",
    description: "Best free AI content generator 2026 for instant results",
  },
  {
    icon: Zap,
    title: "Lightning Fast",
    description: "Get professional results in seconds, not minutes",
  },
  {
    icon: Shield,
    title: "100% Free & Secure",
    description: "No signup required – your data is never stored",
  },
  {
    icon: Globe,
    title: "Works Everywhere",
    description: "Access from any device, any browser, anywhere",
  },
  {
    icon: Clock,
    title: "Always Available",
    description: "24/7 uptime with instant processing",
  },
  {
    icon: Users,
    title: "Trusted by Millions",
    description: "Join thousands of content creators worldwide",
  },
];

// JSON-LD for Organization + WebSite
const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://toolforge2026.com/#organization",
      "name": "ToolForge 2026",
      "url": "https://toolforge2026.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://toolforge2026.com/logo.png"
      },
      "sameAs": []
    },
    {
      "@type": "WebSite",
      "@id": "https://toolforge2026.com/#website",
      "url": "https://toolforge2026.com",
      "name": "ToolForge 2026",
      "description": "Free AI tools 2026: ChatGPT alternative, AI blog writer, TikTok downloader no watermark, hashtag finder, caption generator & more.",
      "publisher": {
        "@id": "https://toolforge2026.com/#organization"
      }
    },
    {
      "@type": "CollectionPage",
      "@id": "https://toolforge2026.com/#webpage",
      "url": "https://toolforge2026.com",
      "name": "ToolForge 2026 – Best Free AI Tools for Content, SEO & TikTok",
      "isPartOf": {
        "@id": "https://toolforge2026.com/#website"
      },
      "about": {
        "@id": "https://toolforge2026.com/#organization"
      },
      "description": "Free AI tools 2026: ChatGPT alternative, AI blog writer, TikTok downloader no watermark, hashtag finder, caption generator & more – all free, no signup.",
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": [
          {
            "@type": "SoftwareApplication",
            "name": "ChatGPT AI Alternative",
            "applicationCategory": "UtilitiesApplication",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
          },
          {
            "@type": "SoftwareApplication",
            "name": "TikTok Downloader No Watermark",
            "applicationCategory": "UtilitiesApplication",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
          },
          {
            "@type": "SoftwareApplication",
            "name": "AI Blog Writer",
            "applicationCategory": "UtilitiesApplication",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
          }
        ]
      }
    }
  ]
};

const Index = () => {
  return (
    <Layout>
      <SEOHead
        title="ToolForge 2026 – Best Free AI Tools for Content, SEO & TikTok"
        description="Free AI tools 2026: ChatGPT alternative, AI blog writer, TikTok downloader no watermark, hashtag finder, caption generator & more – all free, no signup."
        canonical="/"
        jsonLd={jsonLd}
      />
      
      <FreeBanner />
      
      {/* Premium Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 via-purple-900/30 to-emerald-900/50" />
        <div className="absolute inset-0 bg-black/40" />
        
        <picture>
          <source 
            srcSet="/images/hero-home.webp" 
            type="image/webp"
            sizes="100vw"
          />
          <img
            src="/images/hero-home.jpg"
            srcSet="/images/hero-home.jpg 1920w"
            sizes="100vw"
            alt="ToolForge 2026 Free AI Tools Hero Banner"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
            loading="eager"
            fetchPriority="high"
            decoding="sync"
            width={1920}
            height={1080}
          />
        </picture>
        
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto animate-fade-in">
          <div className="inline-flex items-center gap-2 glass px-6 py-3 rounded-full text-sm font-medium mb-8">
            <Sparkles className="h-4 w-4 text-cyan-400" aria-hidden="true" />
            <span className="text-foreground/90">{totalTools.toLocaleString()}+ Free Tools in 10 Hubs</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
            <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              Tool Forge 2026
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-foreground/80 mb-12 max-w-3xl mx-auto leading-relaxed">
            Free Professional AI Tools for Content, SEO & Social Media
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-500 hover:to-emerald-500 text-white font-bold py-6 px-10 rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-300 text-lg"
              asChild
            >
              <a href="#tools">
                Explore Tools Now <ArrowRight className="h-5 w-5 ml-2" aria-hidden="true" />
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="glass border-white/20 text-foreground hover:bg-white/10 py-6 px-10 rounded-xl text-lg font-semibold"
              asChild
            >
              <a href="/chatgpt-ai">
                Try ChatGPT AI
              </a>
            </Button>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 border-b border-white/10" aria-labelledby="features-heading">
        <div className="container">
          <h2 id="features-heading" className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why Choose <span className="text-gradient">ToolForge</span>?
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            The most powerful collection of free AI tools for content creators and marketers
          </p>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <div
                key={index}
                className="glass-card p-6 hover:border-cyan-500/30 transition-all duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-white/10 mb-4">
                  <feature.icon className="h-6 w-6 text-cyan-400" aria-hidden="true" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tool Hubs Grid */}
      <section id="tools" className="py-20" aria-labelledby="tools-heading">
        <div className="container">
          <div className="text-center mb-16 animate-fade-in">
            <h2 id="tools-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              10 Tool Hubs – {totalTools.toLocaleString()}+ Free Tools
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-6">
              {clientTools.toLocaleString()}+ client-side tools (instant, unlimited) + {aiTools} AI-powered tools (30/day free)
            </p>
            <div className="inline-flex items-center gap-2 text-emerald-400 font-semibold">
              <Shield className="h-5 w-5" />
              Zero cost to you – 100% FREE forever
            </div>
          </div>
          
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {toolHubs.map((hub, index) => (
              <div
                key={hub.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <HubCard hub={hub} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/10" aria-labelledby="cta-heading">
        <div className="container">
          <div className="glass-card p-12 md:p-16 text-center max-w-4xl mx-auto">
            <h2 id="cta-heading" className="text-3xl md:text-4xl font-bold mb-6">
              Start Using <span className="text-gradient">Free AI Tools</span> Today
            </h2>
            <p className="text-muted-foreground mb-10 text-lg max-w-2xl mx-auto">
              No signup, no credit card, no hassle. The best free ChatGPT alternative and TikTok tools in 2026.
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-500 hover:to-emerald-500 text-white font-bold py-6 px-12 rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-300 text-lg"
              asChild
            >
              <a href="#tools">
                Get Started Free <ArrowRight className="h-5 w-5 ml-2" aria-hidden="true" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
